/**
 * Raw `convertToAssets(10^shareDecimals)` derived from a vault's totals — how
 * much underlying one share is worth, in raw asset units. Provider-agnostic
 * share price for the ERC-4626 vaults whose data source doesn't surface a
 * `convertToShares`/`convertToAssets`/`pricePerShare` field directly
 * (Morpho/Lista, Silo, Gearbox, Euler-Earn).
 *
 * It's the `totalAssets / totalSupply` ratio scaled by the share unit, so it
 * ignores ERC-4626 virtual-share rounding — a negligible difference for
 * display, and the same quantity the realised-APY recorder reads on-chain via
 * `convertToAssets(1e18)`. Returns `'0'` when supply is zero or inputs are
 * malformed.
 *
 * @param shareDecimals the vault's *share* decimals (e.g. 18 for MetaMorpho,
 *        the asset decimals for offset-free vaults like Silo/Gearbox/Euler).
 */
export function deriveConvertToAssets(
  totalAssetsRaw: string | bigint | number | undefined,
  totalSupplyRaw: string | bigint | number | undefined,
  shareDecimals: number,
): string {
  try {
    const ta = BigInt(totalAssetsRaw ?? 0)
    const ts = BigInt(totalSupplyRaw ?? 0)
    if (ts <= 0n) return '0'
    const dec = Math.max(0, Math.round(Number(shareDecimals) || 0))
    const probe = 10n ** BigInt(dec)
    return ((ta * probe) / ts).toString()
  } catch {
    return '0'
  }
}
